import { useEffect, useState } from 'react';

import { fetchVehicleTrips } from '@/api/vehiclesApi';
import type { VehicleTrip } from '@/api/vehiclesApi';
import { showErrorToast } from '@/errors/showErrorToast';
import { toAppError } from '@/errors/toAppError';

interface VehicleTripsState {
    trips: VehicleTrip[] | null;
    isLoading: boolean;
    isError: boolean;
}

interface UseVehicleTripsResult extends VehicleTripsState {
    retry: () => void;
}

/**
 * Fetches a vehicle's trip history for the Trips tab. No cache — new trips complete every
 * few minutes and a track's status advances server-side, so a reopened tab must refetch.
 * A 403 fires `onForbidden` (capability revoked mid-session) instead of toasting.
 */
export function useVehicleTrips(
    vehicleId: string | null,
    from: string,
    to: string,
    enabled: boolean,
    onForbidden?: () => void,
): UseVehicleTripsResult {
    const [state, setState] = useState<VehicleTripsState>({ trips: null, isLoading: false, isError: false });
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!enabled || vehicleId === null || !from || !to) {
            // eslint-disable-next-line react-hooks/set-state-in-effect, react-x/set-state-in-effect
            setState({ trips: null, isLoading: false, isError: false });
            return;
        }

        const controller = new AbortController();
        // eslint-disable-next-line react-x/set-state-in-effect
        setState({ trips: null, isLoading: true, isError: false });

        fetchVehicleTrips(vehicleId, from, to)
            .then((response) => {
                if (controller.signal.aborted) return;
                setState({ trips: response.items, isLoading: false, isError: false });
            })
            .catch((error: unknown) => {
                // The selection moved on (or unmounted) — discard this stale result silently.
                if (controller.signal.aborted) return;
                if (toAppError(error).code === 'forbidden') {
                    onForbidden?.();
                    return;
                }
                showErrorToast(error);
                setState({ trips: null, isLoading: false, isError: true });
            });

        return () => {
            controller.abort();
        };
        // onForbidden identity is deliberately excluded so a new inline callback can't
        // re-trigger the request.
        // eslint-disable-next-line react-hooks/exhaustive-deps, react-x/exhaustive-deps
    }, [vehicleId, from, to, enabled, attempt]);

    const retry = () => {
        setAttempt((a) => a + 1);
    };

    return { ...state, retry };
}
